import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  ReactConsentContext,
  useSelectedWorkspace,
  useInfo,
} from '../../Flow/ConsentContextProvider';
import type { McpPolicyIntentGroup } from './domain/policyTypes';
import type { TrelloWorkspace } from './domain/trelloTypes';
import { getForgeMcpAppName } from '#definitions/consent-info';
import { getAgentMcpResource } from './domain/agentMcpResource';
import {
  useBitbucketWorkspaces,
  type BitbucketWorkspaceSelection,
} from './domain/useBitbucketWorkspaces';
import { useWorkspacePolicy } from './domain/useWorkspacePolicy';
import { useTrelloPolicy } from './domain/useTrelloPolicy';
import { useTrelloSelection } from './domain/useTrelloSelection';
import { useOrgMatch, type OrgMatch } from './domain/useOrgMatch';
import {
  getBitbucketWorkspaceOrgId,
  resolveSiteOrgId,
} from './domain/orgMatch';
import {
  useMergedToolsets,
  useToolsetSelection,
} from './domain/useToolsetSelection';
import { useMcpFlow } from './domain/mcpFlow';
import { useConsentStep, type ConsentStepState } from './domain/useConsentStep';
import {
  deriveSiteNameFromUrl,
  parseSelectedSiteUrls,
} from './domain/availableSites';
import type { ScopeClassification } from './domain/trelloScopes';
import {
  getCombinedPolicyStatus,
  getPrimaryPolicyErrorSource,
  IDLE_MCP_POLICIES,
  type McpPolicies,
  type McpPolicyErrorKind,
  type McpPolicySource,
  type McpPolicyStatus,
} from './domain/policyState';
import { usePolicyCheckItem } from './domain/usePolicyCheckItem';

export { IDLE_MCP_POLICIES };
export type { McpPolicies, McpPolicySource };

/** The policy checks and the toolsets they return — see usePolicyCheckItem, useMergedToolsets. */
export type McpPolicyContext = {
  mcpPolicyStatus: McpPolicyStatus;
  mcpPolicyErrorKind?: McpPolicyErrorKind | null;
  /** Display name of the workspace that triggered the error, when relevant. */
  mcpPolicyErrorWorkspaceName?: string | null;
  /**
   * Each policy check on its own, so a selector can report its own failures
   * without picking up another source's, and Accept can ask about one source
   * rather than about whichever error happens to be reported.
   */
  policies: McpPolicies;
  /** Intent groups from the API, ordered read → write → search. */
  mcpByIntent: McpPolicyIntentGroup[];
  /** Selected toolset names (raw name from API, e.g. "read_jira"). */
  mcpSelectedToolsetNames: ReadonlySet<string>;
  toggleMcpToolset: (toolsetName: string, selected: boolean) => void;
};

/** Which selectors the flow renders, and how — see useMcpFlow. */
export type McpFlowContext = {
  /** Scope classification: which product lines (Trello / Atlassian) are in the token. */
  scopeClassification: ScopeClassification;
  trelloRequestedButNoAccess: boolean;
  /** Whether MCP renders the Atlassian site selector. */
  showSiteSelector: boolean;
  /** Whether MCP renders the Bitbucket workspace selector. */
  showBitbucketSelector: boolean;
  /**
   * Whether the site and Bitbucket selectors each allow several selections, up
   * to MAX_SELECTABLE_SITES / MAX_SELECTABLE_BITBUCKET_WORKSPACES. Gated, and
   * only in the site, site+Bitbucket and Bitbucket flows.
   */
  isSiteBitbucketMultiSelectEnabled: boolean;
  /**
   * Whether the consent is split into two steps: the site/Bitbucket
   * multi-select is on and so is the two-step consent gate.
   */
  isTwoStepConsentEnabled: boolean;
} & ConsentStepState;

/** Trello workspace selection and policy — see useTrelloSelection, useTrelloPolicy. */
export type McpTrelloContext = {
  /**
   * Whether the request includes account-level Inbox/Planner scopes, which can
   * be granted even when the user has no Trello workspaces.
   */
  hasTrelloInboxPlannerScopes: boolean;
  /** Currently selected Trello workspaces. Mixed-scope requests select one workspace. */
  selectedTrelloWorkspaces: TrelloWorkspace[];
  setSelectedTrelloWorkspaces: (workspaces: TrelloWorkspace[]) => void;
  /** Whether the Trello workspace selector allows more than one workspace. */
  isTrelloWorkspaceMultiSelectEnabled?: boolean;
  /**
   * Maximum number of Trello workspaces that can be selected, or null when the
   * selection is unbounded.
   */
  maxSelectableTrelloWorkspaces?: number | null;
  /** Mock Trello workspaces available for selection (prototype — will be replaced by AGG query). */
  availableTrelloWorkspaces: TrelloWorkspace[];
  /** Whether Trello workspaces are currently being fetched (Relay suspend). */
  trelloWorkspacesLoading: boolean;
  /** True when another page of Trello workspaces is available to fetch. */
  trelloWorkspacesHasMore: boolean;
  /** True while the next page of Trello workspaces is in flight. */
  trelloWorkspacesLoadingMore: boolean;
  /** Requests the next page of Trello workspaces. */
  onLoadMoreTrelloWorkspaces: () => void;
  /**
   * True when the token requested inbox/planner scopes but the selected Trello workspace
   * is outside the user's managing organization, so those scopes are not available.
   */
  inboxPlannerScopesDisabled: boolean;
  /**
   * Trello workspace ids consented to on previous visits, most recent first.
   * Empty for mixed-scope requests or when nothing is stored.
   */
  recentTrelloWorkspaceIds?: string[];
  /** Persists the current Trello selection as recent. Called on Accept. */
  recordConsentedTrelloWorkspaces?: () => void;
};

/** Who is asking, and for what — read straight off the token and `/consent/info`. */
export type McpRequestContext = {
  /** Display name of the client requesting consent, when known. */
  clientName?: string | null;
  /**
   * The signed `resources` Rovo Agent ARI when this is an Agent MCP consent.
   * Agent consent is all-or-nothing on a server-locked site, so no client
   * site, workspace or policy selection applies.
   */
  agentMcpResource?: string;
};

/**
 * One slice per hook the provider folds in: Bitbucket selection
 * (useBitbucketWorkspaces) and org/sandbox matching (useOrgMatch) reuse the
 * hooks' own result types, so a field added there reaches the context here.
 */
export type McpContext = McpPolicyContext &
  McpFlowContext &
  McpTrelloContext &
  McpRequestContext &
  BitbucketWorkspaceSelection &
  OrgMatch;

export const ReactMcpContext = createContext<McpContext | null>(null);

export const useMcpContext = () => {
  const context = useContext(ReactMcpContext);
  if (!context) {
    throw new Error('useMcpContext must be used within McpContextProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const noopLoadMore = () => {};

type McpContextProviderProps = PropsWithChildren<{
  /** Override the list of Trello workspaces (used in tests / Storybook). */
  trelloWorkspaces?: TrelloWorkspace[];
  /** Whether Trello workspaces are currently being fetched (e.g. via Relay suspend). */
  trelloWorkspacesLoading?: boolean;
  /** True when another page of Trello workspaces is available to fetch. */
  trelloWorkspacesHasMore?: boolean;
  /** True while the next page of Trello workspaces is in flight. */
  trelloWorkspacesLoadingMore?: boolean;
  /** Requests the next page of Trello workspaces. */
  onLoadMoreTrelloWorkspaces?: () => void;
  /**
   * Override the previously-consented Trello workspace ids, bypassing
   * localStorage (used in tests / Storybook).
   */
  recentTrelloWorkspaceIds?: string[];
}>;

export const McpContextProvider: FunctionComponent<McpContextProviderProps> = ({
  children,
  trelloWorkspaces,
  trelloWorkspacesLoading = false,
  trelloWorkspacesHasMore = false,
  trelloWorkspacesLoadingMore = false,
  onLoadMoreTrelloWorkspaces = noopLoadMore,
  recentTrelloWorkspaceIds: recentTrelloWorkspaceIdsOverride,
}) => {
  const consentContext = useContext(ReactConsentContext);
  if (!consentContext) {
    throw new Error(
      'McpContextProvider must be used within ConsentContextProvider',
    );
  }

  const { token } = consentContext;
  const info = useInfo();
  const forgeAppName = getForgeMcpAppName(info);
  const agentMcpResource = getAgentMcpResource(token.resources);
  const { selectedWorkspace } = useSelectedWorkspace();
  const { setSelectedWorkspace } = consentContext;

  const isForgeMcp = forgeAppName !== undefined;
  const toolsetSelection = useToolsetSelection();
  const { selectFetchedToolsets, toggleMcpToolset } = toolsetSelection;

  // Bitbucket workspace selection (available list, selection state, and
  // stale-selection cleanup) lives in a dedicated hook and is folded into the
  // context value below.
  const {
    isBitbucketWorkspaceAccessEnabled,
    availableBitbucketWorkspaces,
    selectedBitbucketWorkspaces,
    setSelectedBitbucketWorkspaces,
  } = useBitbucketWorkspaces();

  const {
    scopeClassification,
    trelloRequestedButNoAccess,
    orderedPolicySources,
    showSiteSelector,
    showBitbucketSelector,
    isSiteBitbucketMultiSelectEnabled,
    isTwoStepConsentEnabled,
  } = useMcpFlow({
    token,
    isBitbucketWorkspaceAccessEnabled,
    agentMcpResource,
    isForgeMcp,
  });

  const { consentStep, isSelectStep, isReviewStep, goToReview, goToSelect } =
    useConsentStep(isTwoStepConsentEnabled);

  const selectedSiteUrls = useMemo(
    () => parseSelectedSiteUrls(selectedWorkspace),
    [selectedWorkspace],
  );

  // Site and Bitbucket must grant within a single organization. Only pickers
  // on screen are compared: a Trello-only flow's hidden auto-selected site is
  // not a user choice, so it must never be reported as conflicting.
  const orgMatch = useOrgMatch({
    isMultiSelect: isSiteBitbucketMultiSelectEnabled,
    showSiteSelector,
    showBitbucketSelector,
    selectedSiteUrls,
    selectedBitbucketWorkspaces,
  });

  // One policy check per picker — see usePolicyCheckItem.
  const siteCheckUrl = usePolicyCheckItem(
    selectedSiteUrls,
    isSiteBitbucketMultiSelectEnabled,
    (url) => resolveSiteOrgId(orgMatch.siteOrgIndex, url),
  );
  const bitbucketCheckWorkspace = usePolicyCheckItem(
    selectedBitbucketWorkspaces,
    isSiteBitbucketMultiSelectEnabled,
    getBitbucketWorkspaceOrgId,
  );

  const activeSitePolicyState = useWorkspacePolicy({
    token,
    isEnabled: !agentMcpResource,
    targetId: siteCheckUrl,
    targetName: siteCheckUrl ? deriveSiteNameFromUrl(siteCheckUrl) : null,
    requiresRedirectCallback: true,
    onToolsetsLoaded: selectFetchedToolsets,
  });
  const bitbucketPolicyState = useWorkspacePolicy({
    token,
    isEnabled: isBitbucketWorkspaceAccessEnabled,
    targetId: bitbucketCheckWorkspace?.ari ?? null,
    targetName: bitbucketCheckWorkspace?.name ?? null,
    onToolsetsLoaded: selectFetchedToolsets,
  });

  const clientName = info.client?.name ?? token.clientName ?? null;

  const {
    selectedTrelloWorkspaces,
    setSelectedTrelloWorkspaces,
    availableTrelloWorkspaces,
    isTrelloWorkspaceMultiSelectEnabled,
    maxSelectableTrelloWorkspaces,
    recentTrelloWorkspaceIds,
    recordConsentedTrelloWorkspaces,
    hasInboxPlannerScopes: trelloInboxPlannerScopesRequested,
    trelloPolicyTargets,
  } = useTrelloSelection({
    token,
    scopeClassification,
    trelloWorkspaces,
    recentTrelloWorkspaceIdsOverride,
    selectedWorkspace,
    setSelectedWorkspace,
  });
  const { policy: activeTrelloPolicyState, inboxPlannerScopesDisabled } =
    useTrelloPolicy({
      token,
      isEnabled: scopeClassification.hasTrello && !agentMcpResource,
      targets: trelloPolicyTargets,
      selectedTrelloWorkspaces,
      onToolsetsLoaded: selectFetchedToolsets,
    });

  const { byIntent, selectedToolsetNames } = useMergedToolsets({
    site: activeSitePolicyState,
    trello: activeTrelloPolicyState,
    bitbucket: bitbucketPolicyState,
    orderedPolicySources,
    isForgeMcp,
    selection: toolsetSelection,
  });

  // Leaving multi-select (the gate or the flow changed) keeps only the first
  // site and workspace, so a single-select picker never holds several.
  useEffect(() => {
    if (isSiteBitbucketMultiSelectEnabled) return;
    if (selectedSiteUrls.length > 1) {
      setSelectedWorkspace?.(selectedSiteUrls[0]);
    }
    if (selectedBitbucketWorkspaces.length > 1) {
      setSelectedBitbucketWorkspaces(selectedBitbucketWorkspaces.slice(0, 1));
    }
  }, [
    isSiteBitbucketMultiSelectEnabled,
    selectedSiteUrls,
    selectedBitbucketWorkspaces,
    setSelectedWorkspace,
    setSelectedBitbucketWorkspaces,
  ]);

  const value = useMemo<McpContext>(() => {
    // The three checks, keyed by source. In a Trello-only flow the hidden site
    // check does not speak for the flow; orderedSources handles that scoping.
    const policies: McpPolicies = {
      site: activeSitePolicyState,
      trello: activeTrelloPolicyState,
      bitbucket: bitbucketPolicyState,
    };
    const combinedPolicyStatus: McpPolicyStatus = getCombinedPolicyStatus(
      policies,
      orderedPolicySources,
    );
    // The single failure being reported, resolved once so the kind, message and
    // source can never describe different errors.
    const errorSource = getPrimaryPolicyErrorSource(
      policies,
      orderedPolicySources,
    );
    const errorState = errorSource ? policies[errorSource] : null;
    const errorKind =
      errorState?.status === 'error' ? errorState.errorKind : null;

    return {
      mcpPolicyStatus: combinedPolicyStatus,
      mcpPolicyErrorKind: errorKind,
      mcpPolicyErrorWorkspaceName:
        errorState?.status === 'error'
          ? (errorState.errorWorkspaceName ?? null)
          : null,
      policies,
      hasTrelloInboxPlannerScopes: trelloInboxPlannerScopesRequested,
      mcpByIntent: byIntent,
      mcpSelectedToolsetNames: selectedToolsetNames,
      toggleMcpToolset,
      scopeClassification,
      trelloRequestedButNoAccess,
      selectedTrelloWorkspaces,
      setSelectedTrelloWorkspaces,
      isTrelloWorkspaceMultiSelectEnabled,
      maxSelectableTrelloWorkspaces,
      clientName,
      availableTrelloWorkspaces,
      trelloWorkspacesLoading,
      trelloWorkspacesHasMore,
      trelloWorkspacesLoadingMore,
      onLoadMoreTrelloWorkspaces,
      inboxPlannerScopesDisabled,
      isBitbucketWorkspaceAccessEnabled,
      showSiteSelector,
      showBitbucketSelector,
      availableBitbucketWorkspaces,
      isSiteBitbucketMultiSelectEnabled,
      isTwoStepConsentEnabled,
      consentStep,
      isSelectStep,
      isReviewStep,
      goToReview,
      goToSelect,
      selectedBitbucketWorkspaces,
      setSelectedBitbucketWorkspaces,
      ...orgMatch,
      recentTrelloWorkspaceIds,
      recordConsentedTrelloWorkspaces,
      agentMcpResource,
    };
  }, [
    activeSitePolicyState,
    activeTrelloPolicyState,
    bitbucketPolicyState,
    trelloInboxPlannerScopesRequested,
    byIntent,
    selectedToolsetNames,
    toggleMcpToolset,
    scopeClassification,
    trelloRequestedButNoAccess,
    selectedTrelloWorkspaces,
    setSelectedTrelloWorkspaces,
    isTrelloWorkspaceMultiSelectEnabled,
    maxSelectableTrelloWorkspaces,
    clientName,
    availableTrelloWorkspaces,
    trelloWorkspacesLoading,
    trelloWorkspacesHasMore,
    trelloWorkspacesLoadingMore,
    onLoadMoreTrelloWorkspaces,
    inboxPlannerScopesDisabled,
    isBitbucketWorkspaceAccessEnabled,
    showSiteSelector,
    showBitbucketSelector,
    availableBitbucketWorkspaces,
    isSiteBitbucketMultiSelectEnabled,
    isTwoStepConsentEnabled,
    consentStep,
    isSelectStep,
    isReviewStep,
    goToReview,
    goToSelect,
    selectedBitbucketWorkspaces,
    setSelectedBitbucketWorkspaces,
    orgMatch,
    recentTrelloWorkspaceIds,
    recordConsentedTrelloWorkspaces,
    orderedPolicySources,
    agentMcpResource,
  ]);

  return (
    <ReactMcpContext.Provider value={value}>
      {children}
    </ReactMcpContext.Provider>
  );
};

export default McpContextProvider;
